import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { addStaffNote, NOTE_MAX, updateStaffNote } from "../../services/staffNotes";
import { INTEREST_OPTIONS, NOTE_TYPES, noteFields, noteFormValid, noteType, REPLIED_OPTIONS } from "../../utils/staffNotesPresentation";
import { tokens } from "../../utils/figmaTokens";
import SegmentTabs from "../kit/SegmentTabs";

// A labelled choice: the kit's pill tabs under a small caption.
const Choice = ({ label, tabs, value, onChange }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mb: 2 }}>
    <Typography variant="labelMedium" component="span" sx={{ color: tokens.text.secondary }}>
      {label}
    </Typography>
    <Box sx={{ alignSelf: "flex-start" }}>
      <SegmentTabs label={label} tabs={tabs} value={value} onChange={onChange} />
    </Box>
  </Box>
);

const typeLabel = (type) => NOTE_TYPES.find((t) => t.key === type)?.label || "General note";

// One staff note about this person (spec 059), on SendNotificationDialog's frame. Without
// `note` it adds; with one it edits that note (the author's own — the tab shows Edit to nobody
// else). `initialType` is the type an add opens on (the Students table opens on Contact, D21).
// `contactAllowed` (the student page) offers the Contact type (D8): did they reply, and
// if so are they interested, then what they said — which may be empty. The type never changes
// on an edit. `onSaved(message)` fires once the write landed.
const AddNoteDialog = ({ open, onClose, personUid, actingUser, note = null, contactAllowed = false, initialType = "general", onSaved }) => {
  const [type, setType] = useState("general");
  const [replied, setReplied] = useState(null); // null | true | false
  const [interested, setInterested] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const editing = Boolean(note);
  const contact = type === "contact";
  const trimmed = text.trim();
  const form = { type, text: trimmed, replied, interested };
  const valid = noteFormValid(form, NOTE_MAX);

  // Each opening starts from the note being edited, or blank.
  useEffect(() => {
    if (open) {
      setType(note ? noteType(note) : initialType);
      setReplied(note && noteType(note) === "contact" ? Boolean(note.replied) : null);
      setInterested(note?.interested || "");
      setText(note?.text || "");
      setError("");
    }
  }, [open, note, initialType]);

  const close = () => {
    if (busy) return;
    onClose();
  };
  const save = async () => {
    if (!valid || !personUid) return;
    setBusy(true);
    setError("");
    try {
      const fields = noteFields(form);
      if (editing) await updateStaffNote(personUid, note.id, fields);
      else await addStaffNote(personUid, fields, actingUser || {});
      onClose();
      onSaved(editing ? "Note updated." : "Note added.");
    } catch (e) {
      console.error("Error saving the note:", e);
      setError(e.message || "Failed to save the note.");
    } finally {
      setBusy(false);
    }
  };

  const textLabel = contact ? (replied === false ? "Notes" : "What they said") : "Note";
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 600 }}>{editing ? "Edit note" : "Add note"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only staff see this note. The person is never shown it.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {editing ? (
          <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary, mb: 2 }}>
            {typeLabel(type)}
          </Typography>
        ) : (
          contactAllowed && <Choice label="Type" tabs={NOTE_TYPES} value={type} onChange={setType} />
        )}
        {contact && (
          <>
            <Choice
              label="Did they reply?"
              tabs={REPLIED_OPTIONS}
              value={replied === null ? "" : replied ? "yes" : "no"}
              onChange={(key) => setReplied(key === "yes")}
            />
            {replied === true && <Choice label="Interested?" tabs={INTEREST_OPTIONS} value={interested} onChange={setInterested} />}
          </>
        )}
        <TextField
          label={textLabel}
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          autoFocus={!contact}
          multiline
          minRows={4}
          disabled={busy}
          error={trimmed.length > NOTE_MAX}
          helperText={`${trimmed.length} / ${NOTE_MAX}`}
          slotProps={{ formHelperText: { sx: { textAlign: "right", color: trimmed.length > NOTE_MAX ? tokens.error.ink : undefined } } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNoteDialog;
